import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { queryKeys } from '@/config/query-keys';
import { PricesApi } from '@/lib/prices-api';
import type { PeriodPrice } from '@/types/price';

export type UnitTypePriceSummary = {
  monthly: PeriodPrice | undefined;
  weekly: PeriodPrice | undefined;
  sixMonths: PeriodPrice | undefined;
  elevenMonths: PeriodPrice | undefined;
  currency: string;
};

const BOX_SHOWCASE_PERIODS = ['1w', '1m', '6m', '11m'] as const;

function pickPeriod(items: PeriodPrice[] | undefined, period: string): PeriodPrice | undefined {
  return items?.find((item) => item.period === period);
}

export function useUnitTypesShowcasePrices(
  facilityCode: string | null | undefined,
  productCodes: string[],
) {
  const stableCodes = useMemo(
    () => [...new Set(productCodes.filter(Boolean))].sort(),
    [productCodes],
  );

  const queries = useQueries({
    queries: stableCodes.map((productCode) => {
      const params = {
        productKind: 'unit' as const,
        facility: facilityCode ?? undefined,
        productCode,
        showcase: [...BOX_SHOWCASE_PERIODS],
      };

      return {
        queryKey: queryKeys.prices.showcase(params),
        queryFn: () => PricesApi.getShowcasePrices(params),
        enabled: Boolean(facilityCode && productCode),
        staleTime: 60_000,
        retry: false,
      };
    }),
  });

  const queriesData = queries.map((query) => query.data)
  const queriesLoading = queries.map((query) => query.isLoading)

  const pricesByCode = useMemo(() => {
    const map = new Map<string, UnitTypePriceSummary>();

    stableCodes.forEach((productCode, index) => {
      const data = queriesData[index];
      if (!data) {
        return;
      }

      map.set(productCode, {
        monthly: pickPeriod(data.items, '1m'),
        weekly: pickPeriod(data.items, '1w'),
        sixMonths: pickPeriod(data.items, '6m'),
        elevenMonths: pickPeriod(data.items, '11m'),
        currency: data.currency,
      });
    });

    return map;
  }, [queriesData, stableCodes]);

  const isLoading = Boolean(facilityCode) && queriesLoading.some(Boolean);

  return {
    pricesByCode,
    isLoading,
  };
}

export function formatShowcaseMoney(amount: number): string {
  return `${new Intl.NumberFormat('ru-RU').format(Math.round(amount))} ₽`;
}

export function formatMonthlyPriceLabel(amount: number): string {
  return `${formatShowcaseMoney(amount)}/мес`;
}

export function formatDiscountPercent(total: number, totalBefore: number): number | null {
  if (totalBefore <= total) {
    return null;
  }

  const percent = Math.round((1 - total / totalBefore) * 100);
  return percent > 0 ? percent : null;
}
